import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { bookingAPI, userAPI, companyAPI, userCompanyAPI, adminAPI } from '../services/api';
import Layout from '../components/Layout';
import AvailableSlotsCalendar from '../components/AvailableSlotsCalendar';
import ShopBrowserModal from '../components/ShopBrowserModal';
import Icon from '../components/Icon';
import { metaEvents } from '../utils/facebookPixel';
import '../styles/BookingWizard.css';

function BookingWizardPage({ user, onLogout, embedded = false, darkMode = true, onToggleDarkMode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if user is technician
  const isTechnician = user?.role === 'technician';
  
  // Pre-selected company from URL (when booking from shop profile)
  const preSelectedCompanyId = searchParams.get('companyId');
  const [isCompanyPreSelected, setIsCompanyPreSelected] = useState(false);

  // Step 1: Bike selection
  const [bikes, setBikes] = useState([]);
  const [selectedBikeId, setSelectedBikeId] = useState('');

  // Step 2: Company selection
  const [companies, setCompanies] = useState([]);
  const [favoriteShops, setFavoriteShops] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [bookingNotAvailable, setBookingNotAvailable] = useState(false);
  const [bookingInterestSent, setBookingInterestSent] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [showShopBrowser, setShowShopBrowser] = useState(false);

  // Step 3: Service template selection
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Step 4: Date and time selection
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  
  // Technician preference
  const [hasTechnicianPreference, setHasTechnicianPreference] = useState(false);
  const [availableTechnicians, setAvailableTechnicians] = useState([]);
  const [preferredTechnicianId, setPreferredTechnicianId] = useState('');

  // Booking confirmation state
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBookingDetails, setConfirmedBookingDetails] = useState(null);

  // Error state for tier-related booking failure (company contact info)
  const [bookingErrorCompany, setBookingErrorCompany] = useState(null);

  const totalSteps = 4;

  const steps = [
    { number: 1, title: 'Bike', iconName: 'bike' },
    { number: 2, title: 'Service shop', iconName: 'companies' },
    { number: 3, title: 'Service type', iconName: 'service' },
    { number: 4, title: 'Date & time', iconName: 'calendar' },
  ];

  const formatPhoneForDisplay = (phone) => (phone || '').replace(/^\+\s*/, '');

  useEffect(() => {
    if (isTechnician) {
      // For technicians: fetch their company and company bikes
      fetchTechnicianData();
    } else {
      // For regular users: fetch their bikes and available companies
      fetchBikes();
      fetchCompanies();
    }
  }, [isTechnician]);

  // Refresh bikes when component becomes visible (e.g., after adding a bike)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isTechnician) {
        fetchBikes();
      }
    };
    const handleFocus = () => {
      if (!isTechnician) {
        fetchBikes();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isTechnician]);

  // Handle pre-selected company from URL (e.g., when booking from shop profile)
  useEffect(() => {
    if (preSelectedCompanyId && companies.length > 0 && !isCompanyPreSelected) {
      const company = companies.find(c => String(c.id) === preSelectedCompanyId);
      if (company) {
        // Auto-select the company
        handleShopSelect(company);
        setIsCompanyPreSelected(true);
      } else {
        // Company not in user's connected companies, fetch it directly
        const fetchPreSelectedCompany = async () => {
          try {
            const response = await companyAPI.getCompanyPublicProfile(preSelectedCompanyId);
            const company = response?.data;
            if (company) {
              handleShopSelect(company);
              setIsCompanyPreSelected(true);
            }
          } catch (err) {
            console.error('Error fetching pre-selected company:', err);
          }
        };
        fetchPreSelectedCompany();
      }
    }
  }, [preSelectedCompanyId, companies, isCompanyPreSelected]);

  // Fetch data for technician users
  const fetchTechnicianData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch technician's company
      const companyResp = await companyAPI.getCompanyInfo();
      const techCompany = companyResp.data.company;
      
      if (techCompany) {
        // Auto-select the technician's company
        setSelectedCompanyId(String(techCompany.id));
        setSelectedCompany(techCompany);
        setCompanies([techCompany]);

        // Fetch all bikes registered to the company
        const bikesResp = await adminAPI.getAllBikes();
        const companyBikes = bikesResp.data.bikes || [];
        setBikes(companyBikes);
        
        if (companyBikes.length === 0) {
      setError('No bikes registered in the shop.');
        }
      } else {
        setError('Company information not found');
      }
    } catch (err) {
      console.error('Error loading technician data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId && currentStep >= 3) {
      fetchServiceTemplates(selectedCompanyId);
    }
  }, [selectedCompanyId, currentStep]);

  const fetchBikes = async () => {
    try {
      const response = await userAPI.getMyBikes();
      setBikes(response.data.bikes || []);
      if (response.data.bikes && response.data.bikes.length === 0) {
        setError('You have no registered bikes. Please add a bike first.');
      }
    } catch (err) {
      console.error('Error loading bikes:', err);
      setError('Failed to load bikes.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    setError('');
    try {
      const response = await userCompanyAPI.getAvailableCompanies();
      const list = response.data.companies || [];
      // First try to show companies that are connected/accepted (user's favorites)
      const connected = list.filter(
        (c) => c.connection_status === 'connected' || c.connection_status === 'accepted'
      );
      
      // Set favorites separately
      setFavoriteShops(connected);
      
      // Set all companies for the browse modal
      setCompanies(list);
      
      if (list.length === 0) {
        // If no companies, try to get all active companies
        try {
          const fallbackResp = await companyAPI.getActiveCompanies();
          const allCompanies = fallbackResp.data.companies || [];
          setCompanies(allCompanies);
          if (allCompanies.length === 0) {
            setError('No service shops available.');
          }
        } catch (fallbackErr) {
          setError('No service shops available.');
        }
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      // Try fallback to active companies
      try {
        const fallbackResp = await companyAPI.getActiveCompanies();
        setCompanies(fallbackResp.data.companies || []);
      } catch (fallbackErr) {
        setError('Failed to load service shops');
        setCompanies([]);
      }
    }
  };

  const handleShopSelect = async (shop) => {
    setSelectedCompanyId(String(shop.id));
    setSelectedCompany(shop);
    setBookingErrorCompany(null);
    setServiceTemplates([]);
    setSelectedTemplateId('');
    // Reset technician preference when shop changes
    setHasTechnicianPreference(false);
    setPreferredTechnicianId('');
    setAvailableTechnicians([]);
    // Fetch available technicians for this company
    try {
      const techResp = await bookingAPI.getCompanyTechniciansPublic(shop.id);
      setAvailableTechnicians(techResp.data.technicians || []);
    } catch (err) {
      console.log('Could not fetch technicians:', err);
      setAvailableTechnicians([]);
    }
    // Online booking is Enterprise/Lifetime only (match backend has_feature_access)
    const tier = (shop.subscription_tier || 'basic').toLowerCase();
    const hasBooking = tier === 'enterprise' || tier === 'lifetime';
    setBookingNotAvailable(!hasBooking);
    setBookingInterestSent(false);
    setShowShopBrowser(false);
  };

  const fetchServiceTemplates = async (companyId) => {
    if (!companyId) {
      setServiceTemplates([]);
      return;
    }
    try {
      setError('');
      const response = await bookingAPI.getCompanyServiceTemplates(companyId, true);
      setServiceTemplates(response.data.templates || []);
      if (response.data.templates && response.data.templates.length === 0) {
        setError('No service packages available for this shop.');
      }
    } catch (err) {
      console.error('Error loading service templates:', err);
      const errorMsg = err.response?.data?.error || 'Failed to load service packages.';
      setError(errorMsg);
      setServiceTemplates([]);
    }
  };

  const handleNext = () => {
    setError('');
    setBookingErrorCompany(null);
    if (currentStep === 1 && !selectedBikeId) {
      setError('Please select a bike.');
      return;
    }
    if (currentStep === 2 && !selectedCompanyId) {
      setError('Please select a service shop.');
      return;
    }
    if (currentStep === 3 && !selectedTemplateId && serviceTemplates.length > 0) {
      setError('Please select a service type.');
      return;
    }
    // Allow proceeding if no templates available (will use default)
    if (currentStep < totalSteps) {
      // Skip step 2 if company is pre-selected (from shop profile booking)
      if (currentStep === 1 && isCompanyPreSelected) {
        setCurrentStep(3); // Jump directly to service type selection
      } else {
        setCurrentStep(currentStep + 1);
      }
      // Scroll to top on step change (especially helpful on mobile/phone view)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Skip step 2 if company is pre-selected (from shop profile booking)
      if (currentStep === 3 && isCompanyPreSelected) {
        setCurrentStep(1); // Jump back to bike selection
      } else {
        setCurrentStep(currentStep - 1);
      }
      setError('');
      // Scroll to top on step change (especially helpful on mobile/phone view)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCalendarDateSelect = (dateStr) => {
    // Validate that bike, company, and service are selected before allowing date selection
    if (!selectedBikeId) {
      setError('Please select a bike first.');
      return;
    }
    if (!selectedCompanyId) {
      setError('Please select a service shop first.');
      return;
    }
    if (!selectedTemplateId && serviceTemplates.length > 0) {
      setError('Please select a service type first.');
      return;
    }
    setError(''); // Clear any previous errors
    setSelectedDate(dateStr);
    setSelectedSlot(null);
  };

  const handleCalendarSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBookingErrorCompany(null);

    if (!selectedCompanyId || !selectedBikeId || !selectedDate || !selectedSlot) {
      setError('Please fill all required fields.');
      return;
    }

    // Handle default 1-hour booking when no template selected
    let templateId = selectedTemplateId;
    let useDefaultTemplate = false;
    
    if (selectedTemplateId === 'default-1hour') {
      useDefaultTemplate = true;
      templateId = null; // Will be created on backend
    }

    if (!templateId && !useDefaultTemplate) {
      setError('Please select a service type!');
      return;
    }

    try {
      // Revalidate selected slot right before booking to avoid stale UI/server drift.
      const latestSlotsResp = await bookingAPI.getAvailableSlots(
        parseInt(selectedCompanyId),
        selectedDate,
        useDefaultTemplate ? null : parseInt(templateId),
        selectedSlot.technician_id || null
      );
      const latestSlots = latestSlotsResp?.data?.available_slots || [];
      const matchingSlot = latestSlots.find((slot) => {
        const sameStart = slot.start === selectedSlot.start;
        const sameEnd = !selectedSlot.end || !slot.end || slot.end === selectedSlot.end;
        const selectedTech = selectedSlot.technician_id || null;
        const slotTech = slot.technician_id || null;
        if (selectedTech) {
          return sameStart && selectedTech === slotTech;
        }
        return sameStart && sameEnd;
      });

      if (!matchingSlot) {
        setSelectedSlot(null);
        setError('Selected time slot is no longer available. Please choose another slot.');
        return;
      }

      const bookingData = {
        bike_id: parseInt(selectedBikeId),
        booking_date: selectedDate,
        start_time: matchingSlot.start,
        technician_id: matchingSlot.technician_id || null,
        notes: notes || null
      };
      
      if (useDefaultTemplate) {
        bookingData.use_default_template = true;
      } else {
        bookingData.service_template_id = parseInt(templateId);
      }

      const response = await bookingAPI.createBooking(parseInt(selectedCompanyId), bookingData);
      
      // Get the selected bike and template details for display
      const selectedBike = bikes.find(b => String(b.id) === selectedBikeId);
      const selectedTemplate = serviceTemplates.find(t => String(t.id) === selectedTemplateId);
      
      // Store booking confirmation details
      setConfirmedBookingDetails({
        bookingId: response?.data?.booking?.id || 'New',
        date: selectedDate,
        startTime: selectedSlot.start,
        endTime: selectedSlot.end,
        companyName: selectedCompany?.name || 'Service Provider',
        companyAddress: selectedCompany?.address || '',
        bikeName: selectedBike ? `${selectedBike.brand} ${selectedBike.model}` : 'Your bike',
        serviceName: selectedTemplate?.name || 'General Service'
      });
      metaEvents.schedule({ content_name: selectedTemplate?.name || 'Service' });
      setBookingConfirmed(true);
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error || 'Failed to create booking.';
      setError(msg);
      if (err.response?.status === 403 && data?.code === 'booking_not_available' && data?.company) {
        setBookingErrorCompany(data.company);
      } else {
        setBookingErrorCompany(null);
      }
    }
  };

  // Generate Google Calendar link for the booking
  const generateCalendarLink = () => {
    if (!confirmedBookingDetails) return '#';
    
    const { date, startTime, endTime, companyName, companyAddress, bikeName, serviceName } = confirmedBookingDetails;
    
    // Format dates for Google Calendar (YYYYMMDDTHHMMSS)
    const startDateTime = `${date.replace(/-/g, '')}T${startTime.replace(/:/g, '')}00`;
    const endDateTime = `${date.replace(/-/g, '')}T${endTime.replace(/:/g, '')}00`;
    
    const title = encodeURIComponent(`${serviceName} - ${bikeName}`);
    const details = encodeURIComponent(`Bike: ${bikeName}\nService: ${serviceName}\nShop: ${companyName}`);
    const location = encodeURIComponent(companyAddress);
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateTime}/${endDateTime}&details=${details}&location=${location}`;
  };

  // Download ICS file for other calendar apps
  const downloadICSFile = () => {
    if (!confirmedBookingDetails) return;
    
    const { date, startTime, endTime, companyName, companyAddress, bikeName, serviceName } = confirmedBookingDetails;
    
    const startDateTime = `${date.replace(/-/g, '')}T${startTime.replace(/:/g, '')}00`;
    const endDateTime = `${date.replace(/-/g, '')}T${endTime.replace(/:/g, '')}00`;
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//FrameBook//Booking//EN
BEGIN:VEVENT
DTSTART:${startDateTime}
DTEND:${endDateTime}
SUMMARY:${serviceName} - ${bikeName}
DESCRIPTION:Bike: ${bikeName}\\nService: ${serviceName}\\nShop: ${companyName}
LOCATION:${companyAddress}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'booking.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="booking-step-content">
            <h2>{isTechnician ? 'Select a bike from your shop' : 'Select a bike'}</h2>
            <p>{isTechnician 
              ? 'All bikes registered to your shop' 
              : 'Which bike do you want to book for?'}</p>
            {bikes.length === 0 ? (
              <div className="empty-state">
                <Icon name="bike" color="muted" darkMode={darkMode} size={64} style={{ marginBottom: '16px' }} />
                <p>{isTechnician ? 'No bikes registered in the shop' : 'No bikes registered yet'}</p>
                {!isTechnician && <Link to="/my-bikes" className="btn btn-primary">Add a bike</Link>}
                {isTechnician && (
                    <p style={{ fontSize: '0.9rem', marginTop: '10px', color: 'rgba(255,255,255,0.5)' }}>
                    Bikes must be registered by customers
                  </p>
                )}
              </div>
            ) : (
              <>
                {isTechnician && (
                  <div style={{
                    background: 'rgba(61, 90, 32, 0.1)',
                    border: '1px solid rgba(61, 90, 32, 0.3)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <Icon name="details" color="primary" darkMode={darkMode} size={20} />
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                      <strong style={{ color: '#3d5a20' }}>{bikes.length}</strong> bikes available in the shop
                    </span>
                  </div>
                )}
                <div className="bike-grid">
                  {bikes.map(bike => (
                    <div
                      key={bike.id}
                      className={`bike-card ${selectedBikeId === String(bike.id) ? 'selected' : ''}`}
                      onClick={() => setSelectedBikeId(String(bike.id))}
                    >
                      <div className="bike-card-icon">
                        <Icon name="bike" color="primary" darkMode={darkMode} size={40} />
                      </div>
                      <div className="bike-card-info">
                        <h3>{bike.brand} {bike.model}</h3>
                        <p className="bike-card-details">
                        {bike.year && <span>Year: {bike.year}</span>}
                          {bike.vin && <span>Frame: {bike.vin}</span>}
                        </p>
                        {/* Show owner info for technicians */}
                        {isTechnician && (bike.owner_firstname || bike.owner_surname || bike.owner_username) && (
                          <p className="bike-card-details" style={{ 
                            marginTop: '8px', 
                            paddingTop: '8px', 
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                    <Icon name="profile" color="secondary" darkMode={darkMode} size={14} />
                            <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                              {bike.owner_firstname && bike.owner_surname 
                                ? `${bike.owner_firstname} ${bike.owner_surname}`
                                : bike.owner_username || 'Unknown owner'}
                            </span>
                          </p>
                        )}
                      </div>
                      {selectedBikeId === String(bike.id) && (
                        <div className="bike-card-check">
                          <Icon name="success" color="success" darkMode={darkMode} size={20} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case 2:
        return (
          <div className="booking-step-content">
            <h2>{isTechnician ? 'Your service shop' : 'Select a service shop'}</h2>
            <p>{isTechnician ? 'This booking will belong to your shop below' : 'Which shop would you like to book at?'}</p>
            {companies.length === 0 ? (
              <div className="empty-state">
                <Icon name="companies" color="muted" darkMode={darkMode} size={64} style={{ marginBottom: '16px' }} />
                <p>No connected service shop</p>
                <p style={{ fontSize: '0.9rem', marginTop: '10px', color: 'rgba(255,255,255,0.5)' }}>
                  {isTechnician 
                    ? 'Please contact your administrator.' 
                    : 'Please connect to a service shop from your profile first.'}
                </p>
              </div>
            ) : isTechnician ? (
              // For technicians: show their company with a nice auto-selected state (no change button)
              <div className="company-grid" style={{ maxWidth: '450px', margin: '0 auto' }}>
                <div className="company-card selected" style={{ cursor: 'default' }}>
                  <div className="company-card-badge" style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #3d5a20 0%, #4a7028 100%)',
                    color: '#fff',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(61, 90, 32, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Icon name="technician" color="primary" darkMode={darkMode} size={16} />
                    Your workshop
                  </div>
                  <div className="company-card-icon" style={{ marginTop: '8px' }}>
                    <Icon name="companies" color="primary" darkMode={darkMode} size={72} />
                  </div>
                  <div className="company-card-info">
                    <h3 style={{ fontSize: '1.5rem', marginBottom: '12px' }}>{selectedCompany?.name}</h3>
                    {selectedCompany?.email && (
                      <p className="company-card-email" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                        <Icon name="email" color="secondary" darkMode={darkMode} size={14} />
                        {selectedCompany.email}
                      </p>
                    )}
                    {selectedCompany?.phone && (
                      <p className="company-card-phone" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                        <Icon name="view" color="secondary" darkMode={darkMode} size={14} />
                        {formatPhoneForDisplay(selectedCompany.phone)}
                      </p>
                    )}
                  </div>
                  <div className="company-card-check">
                    <Icon name="success" color="success" darkMode={darkMode} size={20} />
                  </div>
                </div>
              </div>
            ) : selectedCompanyId ? (
              // Show only selected company (for regular users)
              <div className="company-grid" style={{ maxWidth: '400px', margin: '0 auto' }}>
                {companies
                  .filter(c => String(c.id) === selectedCompanyId)
                  .map(company => (
                    <div
                      key={company.id}
                      className="company-card selected"
                    >
                      <div className="company-card-icon">
                        <Icon name="companies" color="primary" darkMode={darkMode} size={40} />
                      </div>
                      <div className="company-card-info">
                        <h3>{company.name}</h3>
                        {company.email && <p className="company-card-email">{company.email}</p>}
                        {company.phone && <p className="company-card-phone">{formatPhoneForDisplay(company.phone)}</p>}
                      </div>
                      <div className="company-card-check">
                        <Icon name="success" color="success" darkMode={darkMode} size={20} />
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCompanyId('');
                          setSelectedCompany(null);
                          setServiceTemplates([]);
                          setSelectedTemplateId('');
                        }}
                        style={{ 
                          marginTop: '16px', 
                          width: '100%',
                          padding: '8px',
                          fontSize: '13px',
                          opacity: 0.7
                        }}
                      >
                        ✕ Clear selection
                      </button>
                    </div>
                  ))}
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedCompanyId('');
                      setSelectedCompany(null);
                      setServiceTemplates([]);
                      setSelectedTemplateId('');
                    }}
                    style={{ 
                      padding: '10px 20px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center'
                    }}
                  >
                    <Icon name="view" color="secondary" darkMode={darkMode} size={16} />
                    Choose another shop
                  </button>
                </div>
              </div>
            ) : favoriteShops.length > 0 ? (
              // Show favorites first
              <>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#7B9663', fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="success" color="brand" darkMode={darkMode} size={16} />
                    Your Favorite Shops
                  </h3>
                </div>
                <div className="company-grid">
                  {favoriteShops.map(company => (
                    <div
                      key={company.id}
                      className="company-card"
                      onClick={() => handleShopSelect(company)}
                    >
                      <div className="company-card-icon">
                        <Icon name="companies" color="primary" darkMode={darkMode} size={40} />
                      </div>
                      <div className="company-card-info">
                        <h3>{company.name}</h3>
                        {company.email && <p className="company-card-email">{company.email}</p>}
                        {company.phone && <p className="company-card-phone">{formatPhoneForDisplay(company.phone)}</p>}
                      </div>
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(61, 90, 32, 0.2)',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        color: '#7B9663'
                      }}>
                        ★ Favorite
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowShopBrowser(true)}
                    style={{ 
                      padding: '12px 24px',
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Icon name="view" color="secondary" darkMode={darkMode} size={16} />
                    Browse More Service Shops
                  </button>
                </div>
              </>
            ) : (
              // No favorites - show browse button
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Icon name="companies" color="muted" darkMode={darkMode} size={64} style={{ marginBottom: '20px' }} />
                <h3 style={{ color: '#fff', marginBottom: '12px' }}>Find a Service Shop</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                  Browse through our network of service shops. Search by location, ratings, or name to find the perfect shop for your bike.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowShopBrowser(true)}
                  style={{ 
                    padding: '14px 28px',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <Icon name="view" color="primary" darkMode={darkMode} size={18} />
                  Find Service Shops
                </button>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="booking-step-content">
            <h2>Select Service Type</h2>
            <p>What type of service do you need?</p>

            {/* Technician Preference - shown before service type selection */}
            {selectedCompanyId && !bookingNotAvailable && availableTechnicians.length > 0 && (
              <div style={{
                background: darkMode ? 'var(--bg-overlay-light)' : 'rgba(61, 90, 32, 0.06)',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(61, 90, 32, 0.15)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  color: darkMode ? '#ffffff' : '#1a1a1a',
                  fontSize: '15px',
                  fontWeight: '500',
                  marginBottom: hasTechnicianPreference ? '16px' : '0'
                }}>
                  <input
                    type="checkbox"
                    checked={hasTechnicianPreference}
                    onChange={(e) => {
                      setHasTechnicianPreference(e.target.checked);
                      if (!e.target.checked) {
                        setPreferredTechnicianId('');
                      }
                    }}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: 'var(--lime-primary)',
                      cursor: 'pointer'
                    }}
                  />
                  <Icon name="technician" color="primary" darkMode={darkMode} size={18} />
                  Do you have a preference for a technician?
                </label>
                
                {hasTechnicianPreference && (
                  <div style={{ paddingLeft: '30px' }}>
                    <select
                      value={preferredTechnicianId}
                      onChange={(e) => setPreferredTechnicianId(e.target.value)}
                      className="form-input"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '15px',
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                        color: darkMode ? '#ffffff' : '#1a1a1a',
                        border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                        borderRadius: '8px'
                      }}
                    >
                      <option value="">-- Select a technician --</option>
                      {availableTechnicians.map(tech => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                    </select>
                    <p style={{
                      margin: '8px 0 0 0',
                      fontSize: '12px',
                      color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                    }}>
                      Only this technician's available time slots will be shown in the calendar.
                    </p>
                  </div>
                )}
              </div>
            )}

            {!selectedCompanyId ? (
              <div className="empty-state">
                <p>Please select a service shop first!</p>
              </div>
            ) : bookingNotAvailable ? (
              <div className="empty-state" style={{ 
                background: 'rgba(245, 158, 11, 0.1)', 
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '16px',
                padding: '32px',
                textAlign: 'center'
              }}>
                <Icon name="calendar" color="warning" darkMode={darkMode} size={64} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ color: '#f59e0b', marginBottom: '12px' }}>Online Booking Not Available</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '24px' }}>
                  Unfortunately, <strong>{selectedCompany?.name}</strong> does not use online booking yet.
                </p>
                {bookingInterestSent ? (
                  <div style={{ 
                    background: 'rgba(34, 197, 94, 0.15)', 
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '12px',
                    padding: '16px',
                    color: '#22c55e'
                  }}>
                    <Icon name="success" color="success" darkMode={darkMode} size={24} style={{ marginBottom: '8px' }} />
                    <p style={{ margin: 0 }}>Thank you! We've notified the shop about your interest.</p>
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px' }}>
                      Let them know you'd like online booking in the future:
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={sendingInterest}
                      onClick={async () => {
                        setSendingInterest(true);
                        try {
                          await bookingAPI.sendBookingInterest(parseInt(selectedCompanyId));
                          setBookingInterestSent(true);
                        } catch (err) {
                          console.error('Failed to send booking interest', err);
                          setError('Failed to send request. Please try again later.');
                        } finally {
                          setSendingInterest(false);
                        }
                      }}
                      style={{ gap: '8px' }}
                    >
                      <Icon name="notification" color="primary" darkMode={darkMode} size={16} />
                      {sendingInterest ? 'Sending...' : 'I want online booking!'}
                    </button>
                  </>
                )}
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
                    In the meantime, contact them directly:
                  </p>
                  {selectedCompany?.phone && (
                    <a href={`tel:${selectedCompany.phone}`} style={{ color: '#4A8C18', display: 'block', marginTop: '8px' }}>
                      <Icon name="view" color="brand" darkMode={darkMode} size={14} style={{ marginRight: '6px' }} />
                      {formatPhoneForDisplay(selectedCompany.phone)}
                    </a>
                  )}
                  {selectedCompany?.email && (
                    <a href={`mailto:${selectedCompany.email}`} style={{ color: '#4A8C18', display: 'block', marginTop: '8px' }}>
                      <Icon name="email" color="brand" darkMode={darkMode} size={14} style={{ marginRight: '6px' }} />
                      {selectedCompany.email}
                    </a>
                  )}
                </div>
              </div>
            ) : serviceTemplates.length === 0 ? (
              <div className="empty-state">
                <Icon name="service" color="muted" darkMode={darkMode} size={64} style={{ marginBottom: '16px' }} />
                <p>No service packages available</p>
                <p style={{fontSize: '14px', marginTop: '12px', color: 'rgba(255,255,255,0.6)'}}>
                  Use default 1-hour booking slot
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    // Create a default 1-hour template for booking
                    setSelectedTemplateId('default-1hour');
                    setError('');
                  }}
                  style={{marginTop: '16px'}}
                >
                  Continue booking (1 hour)
                </button>
              </div>
            ) : selectedTemplateId ? (
              // Show only selected template
              <div className="service-template-grid" style={{ maxWidth: '500px', margin: '0 auto' }}>
                {serviceTemplates
                  .filter(t => String(t.id) === selectedTemplateId)
                  .map(template => (
                    <div
                      key={template.id}
                      className="service-template-card selected"
                    >
                      <div className="service-template-header">
                        <div className="service-template-icon">
                          <Icon name="service" color="primary" darkMode={darkMode} size={32} />
                        </div>
                        <h3>{template.name}</h3>
                      </div>
                      {template.description && (
                        <p className="service-template-description">{template.description}</p>
                      )}
                      <div className="service-template-details">
                        <div className="service-template-duration">
                          <span className="detail-label">Duration:</span>
                          <span className="detail-value">{template.estimated_duration_minutes} min</span>
                        </div>
                        {template.default_price && (
                          <div className="service-template-price">
                            <span className="detail-label">Price:</span>
                            <span className="detail-value price">£{Number(template.default_price).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <div className="service-template-check">
                        <Icon name="success" color="success" darkMode={darkMode} size={20} />
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTemplateId('');
                          setSelectedDate('');
                          setSelectedSlot(null);
                        }}
                        style={{ 
                          marginTop: '16px', 
                          width: '100%',
                          padding: '8px',
                          fontSize: '13px',
                          opacity: 0.7
                        }}
                      >
                        ✕ Clear selection
                      </button>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="service-template-grid">
                {serviceTemplates.map(template => (
                  <div
                    key={template.id}
                    className="service-template-card"
                    onClick={() => setSelectedTemplateId(String(template.id))}
                  >
                    <div className="service-template-header">
                      <div className="service-template-icon">
                        <Icon name="service" color="primary" darkMode={darkMode} size={32} />
                      </div>
                      <h3>{template.name}</h3>
                    </div>
                    {template.description && (
                      <p className="service-template-description">{template.description}</p>
                    )}
                    <div className="service-template-details">
                      <div className="service-template-duration">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">{template.estimated_duration_minutes} min</span>
                      </div>
                      {template.default_price && (
                        <div className="service-template-price">
                          <span className="detail-label">Price:</span>
                          <span className="detail-value price">£{Number(template.default_price).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="booking-step-content">
            <h2>Select Date & Time</h2>
            <p>When would you like to book your appointment?</p>
            {!selectedCompanyId ? (
              <div className="empty-state">
                <p>Please select a service shop and service type first!</p>
              </div>
            ) : (
              <>
                {/* Technician preference reminder in step 4 */}
                {hasTechnicianPreference && preferredTechnicianId && (
                  <div style={{
                    background: 'rgba(61, 90, 32, 0.1)',
                    border: '1px solid rgba(61, 90, 32, 0.3)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '14px',
                    color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'
                  }}>
                    <Icon name="technician" color="primary" darkMode={darkMode} size={16} />
                    <span>
                      Showing slots for: <strong style={{ color: '#4A8C18' }}>
                        {availableTechnicians.find(t => String(t.id) === String(preferredTechnicianId))?.name || 'Selected technician'}
                      </strong>
                    </span>
                  </div>
                )}
                
                <div className="date-time-selection">
                  <AvailableSlotsCalendar
                    companyId={selectedCompanyId}
                    serviceTemplateId={selectedTemplateId === 'default-1hour' ? null : selectedTemplateId}
                    selectedDate={selectedDate}
                    onDateSelect={handleCalendarDateSelect}
                    onSlotSelect={handleCalendarSlotSelect}
                    preferredTechnicianId={hasTechnicianPreference && preferredTechnicianId ? parseInt(preferredTechnicianId) : null}
                  />
                  {selectedTemplateId === 'default-1hour' && (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(61, 90, 32, 0.1)',
                      border: '1px solid rgba(61, 90, 32, 0.3)',
                      borderRadius: '8px',
                      marginTop: '16px',
                      textAlign: 'center'
                    }}>
                      <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <Icon name="calendar" color="primary" darkMode={darkMode} size={16} />
                        Default 1-hour booking slot
                      </p>
                    </div>
                  )}
                </div>
                <div className="notes-section">
                  <label htmlFor="notes">Notes (optional)</label>
                  <textarea
                    id="notes"
                    className="form-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                    placeholder="Special requests, notes..."
                    style={{ resize: 'vertical' }}
                  />
                </div>
                {selectedSlot && (
                  <div className="selected-slot-info">
                    <h3>Selected appointment:</h3>
                    <p>
                      <strong>Date:</strong> {new Date(selectedDate).toLocaleDateString('en-US')}
                    </p>
                    <p>
                      <strong>Time:</strong> {selectedSlot.start?.substring(0, 5)} - {selectedSlot.end?.substring(0, 5)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="booking-wizard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const content = (
    <>
    {/* Booking Confirmation Overlay - Portal so it appears above sidebar */}
    {bookingConfirmed && confirmedBookingDetails && ReactDOM.createPortal(
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: darkMode
          ? 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(74, 140, 24, 0.15) 0%, transparent 50%), rgba(0, 0, 0, 0.92)'
          : 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(74, 140, 24, 0.08) 0%, transparent 50%), rgba(0, 0, 0, 0.06)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100001,
        padding: '24px',
        overflowY: 'auto'
      }}>
        <div style={{
          background: darkMode ? 'linear-gradient(145deg, #1a2d10 0%, #0f1a0a 100%)' : 'linear-gradient(145deg, #ffffff 0%, #f8faf5 100%)',
          border: darkMode ? '2px solid rgba(74, 140, 24, 0.4)' : '2px solid rgba(74, 140, 24, 0.25)',
          borderRadius: '24px',
          padding: '40px 44px',
          maxWidth: '720px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(74, 140, 24, 0.1)'
        }}>
          {/* Success Icon */}
          <div style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: 'linear-gradient(145deg, #4A8C18 0%, #3d5a20 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 8px 32px rgba(74, 140, 24, 0.4)'
          }}>
            <Icon name="success" customColor="#ffffff" size={52} />
          </div>

          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: darkMode ? '#ffffff' : '#1f2937',
            margin: '0 0 8px 0',
            letterSpacing: '-0.3px'
          }}>
            Booking Confirmed!
          </h1>
          <p style={{
            fontSize: '16px',
            color: darkMode ? 'rgba(255,255,255,0.85)' : '#6b7280',
            margin: '0 0 28px 0'
          }}>
            Your appointment has been scheduled. A confirmation email will be sent shortly.
          </p>

          {/* Booking Details - 2-column grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <div style={{
              background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(74, 140, 24, 0.06)',
              borderRadius: '16px',
              padding: '20px',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(74, 140, 24, 0.12)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: darkMode ? 'rgba(74, 140, 24, 0.2)' : 'rgba(74, 140, 24, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon name="calendar" color="primary" darkMode={darkMode} size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date & Time</div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: darkMode ? '#ffffff' : '#1f2937' }}>
                  {new Date(confirmedBookingDetails.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#4A8C18' }}>
                  {confirmedBookingDetails.startTime?.substring(0, 5)} – {confirmedBookingDetails.endTime?.substring(0, 5)}
                </div>
              </div>
            </div>

            <div style={{
              background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(74, 140, 24, 0.06)',
              borderRadius: '16px',
              padding: '20px',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(74, 140, 24, 0.12)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: darkMode ? 'rgba(74, 140, 24, 0.2)' : 'rgba(74, 140, 24, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon name="companies" color="primary" darkMode={darkMode} size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Service Shop</div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: darkMode ? '#ffffff' : '#1f2937' }}>
                  {confirmedBookingDetails.companyName}
                </div>
              </div>
            </div>

            <div style={{
              background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(74, 140, 24, 0.06)',
              borderRadius: '16px',
              padding: '20px',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(74, 140, 24, 0.12)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              gridColumn: '1 / -1'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: darkMode ? 'rgba(74, 140, 24, 0.2)' : 'rgba(74, 140, 24, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon name="bike" color="primary" darkMode={darkMode} size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bike & Service</div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: darkMode ? '#ffffff' : '#1f2937' }}>
                  {confirmedBookingDetails.bikeName} · {confirmedBookingDetails.serviceName}
                </div>
              </div>
            </div>
          </div>

          {/* Add to Calendar */}
          <div style={{
            background: darkMode ? 'rgba(66, 133, 244, 0.08)' : 'rgba(66, 133, 244, 0.06)',
            borderRadius: '16px',
            padding: '20px 24px',
            marginBottom: '24px',
            border: darkMode ? '1px solid rgba(66, 133, 244, 0.2)' : '1px solid rgba(66, 133, 244, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', justifyContent: 'center' }}>
              <Icon name="calendar" customColor="#4285f4" size={22} />
              <span style={{ fontSize: '15px', fontWeight: '600', color: darkMode ? '#ffffff' : '#1f2937' }}>Add to calendar</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href={generateCalendarLink()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#4285f4',
                  color: '#ffffff',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 2px 8px rgba(66, 133, 244, 0.3)'
                }}
              >
                <Icon name="calendar" customColor="#ffffff" size={18} />
                Google Calendar
              </a>
              <button
                onClick={downloadICSFile}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  color: darkMode ? '#ffffff' : '#1f2937',
                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                <Icon name="download" darkMode={darkMode} size={18} />
                Download .ics
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
              style={{
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px'
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="dashboard" customColor="#ffffff" size={20} />
                Go to Dashboard
              </span>
            </button>
            <button
              onClick={() => { setBookingConfirmed(false); navigate('/calendar'); }}
              className="btn btn-secondary"
              style={{
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px'
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="calendar" darkMode={darkMode} size={20} />
                View My Bookings
              </span>
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    <div className="booking-wizard">
      {error && (
        <div className="error-message" style={{ marginBottom: bookingErrorCompany ? '16px' : undefined }}>
          {error}
          {bookingErrorCompany && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'rgba(245, 158, 11, 0.15)',
              borderRadius: '12px',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              textAlign: 'left'
            }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: 600, color: 'inherit' }}>
                Contact {bookingErrorCompany.name} directly:
              </p>
              {bookingErrorCompany.phone && (
                <a href={`tel:${bookingErrorCompany.phone}`} style={{ color: '#4A8C18', display: 'block', marginBottom: '8px' }}>
                  📞 {formatPhoneForDisplay(bookingErrorCompany.phone)}
                </a>
              )}
              {bookingErrorCompany.email && (
                <a href={`mailto:${bookingErrorCompany.email}`} style={{ color: '#4A8C18', display: 'block', marginBottom: '8px' }}>
                  ✉ {bookingErrorCompany.email}
                </a>
              )}
              {bookingErrorCompany.address && (
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                  📍 {bookingErrorCompany.address}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {success && <div className="success-message">{success}</div>}

      <div className="booking-wizard-container">
        <div className="booking-wizard-header">
          <div className="booking-progress">
            {steps.map((step) => {
              // Step 2 (shop) should appear completed if company is pre-selected
              const isStepCompleted = currentStep > step.number || 
                (step.number === 2 && isCompanyPreSelected);
              const isStepActive = currentStep >= step.number || 
                (step.number === 2 && isCompanyPreSelected);
              
              return (
                <div
                  key={step.number}
                  className={`progress-step ${isStepActive ? 'active' : ''} ${
                    isStepCompleted ? 'completed' : ''
                  }`}
                  style={step.number === 2 && isCompanyPreSelected ? { opacity: 0.7 } : {}}
                >
                  <div className="progress-step-number">
                    {isStepCompleted ? (
                      <Icon name="success" color="success" darkMode={darkMode} size={24} />
                    ) : (
                      <Icon name={step.iconName} color={isStepActive ? "primary" : "secondary"} darkMode={darkMode} size={24} />
                    )}
                  </div>
                  <div className="progress-step-label">
                    {step.number === 2 && isCompanyPreSelected ? (
                      <span title={`Pre-selected: ${selectedCompany?.name || ''}`}>
                        {step.title} ✓
                      </span>
                    ) : (
                      step.title
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="booking-wizard-body">
          {renderStepContent()}
        </div>

        <div className="booking-wizard-footer">
          <div className="booking-wizard-footer-actions">
            {currentStep > 1 && (
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
            <div className="booking-wizard-footer-spacer"></div>
            {currentStep < totalSteps ? (
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleNext}
                disabled={currentStep === 3 && bookingNotAvailable}
                style={currentStep === 3 && bookingNotAvailable ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                Next →
              </button>
            ) : (
              <form onSubmit={handleBooking} style={{ display: 'inline' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!selectedSlot}
                >
                  Confirm booking
                </button>
              </form>
            )}
            <Link to="/dashboard" className="btn btn-secondary">
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
    
    <ShopBrowserModal
      isOpen={showShopBrowser}
      onClose={() => setShowShopBrowser(false)}
      onSelectShop={handleShopSelect}
      darkMode={darkMode}
    />
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout user={user} onLogout={onLogout} pageTitle="Service Booking" pageSubtitle="Book a service step by step" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      {content}
    </Layout>
  );
}

export default BookingWizardPage;
